import type {
  AiCredits,
  AiProcessingStats,
  AiProcessingSummary,
  ClassifyResultsResponse,
} from '@/api/ai/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

export async function fetchAiCredits(): Promise<{ data: AiCredits } | null> {
  try {
    return await apiFetch<{ data: AiCredits }>(
      '/tracing-ai/summary/workflow-trace/total',
      {},
      BACKEND_URL,
    );
  } catch {
    return null;
  }
}

export async function fetchAiProcessingStats(): Promise<AiProcessingStats | null> {
  try {
    const res = await apiFetch<{ data: AiProcessingStats }>(
      '/ai-processing-status/stats',
      {},
      BACKEND_URL,
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function fetchAiProcessingSummary(
  filter?: string,
): Promise<AiProcessingSummary | null> {
  try {
    const qs = filter && filter !== 'ALL' ? `?sortByStatus=${filter}` : '';
    const res = await apiFetch<{ data: AiProcessingSummary }>(
      `/ai-processing-status/summary${qs}`,
      {},
      BACKEND_URL,
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function dismissAiProcessingItem(id: string): Promise<void> {
  await apiFetch<void>(
    `/ai-processing-status/${id}/dismiss`,
    { method: 'PATCH' },
    BACKEND_URL,
  );
}

// Dismiss a queue item by its asset id rather than the AI-processing-status row
// id. For AUDIT jobs the row's `assetId` IS the CAPA `workflowId`, so callers
// that only hold the workflow id (e.g. the audit-AI dialog finishing its flow)
// can clear the card without first looking up the row id.
export async function dismissAiProcessingItemByAsset(
  assetId: string,
): Promise<void> {
  await apiFetch<void>(
    `/ai-processing-status/asset/${assetId}/dismiss`,
    { method: 'PATCH' },
    BACKEND_URL,
  );
}

export async function classifyDropBoxFiles(fileIds: string[]): Promise<void> {
  await apiFetch<void>(
    '/files/drop-box/classify',
    { method: 'POST', body: JSON.stringify({ fileIds }) },
    BACKEND_URL,
  );
}

export async function acceptDropBoxFiles(
  files: { fileId: string; acceptedType: string }[],
): Promise<void> {
  await apiFetch<void>(
    '/files/drop-box/accept',
    { method: 'POST', body: JSON.stringify({ files }) },
    BACKEND_URL,
  );
}

export async function getClassifyResults(
  fileIds: string[],
): Promise<ClassifyResultsResponse> {
  const res = await apiFetch<{ data: ClassifyResultsResponse }>(
    '/files/drop-box/classify/results',
    { method: 'POST', body: JSON.stringify({ fileIds }) },
    BACKEND_URL,
  );
  return res.data;
}
